import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';

import { useI18n } from '@app-velocity/i18n';
import {
  Button,
  ButtonsContainer,
  Modal,
  Typography,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { useDeleteFigmaReportMutation } from '../../../../query/useDeleteFigmaReportMutation';

export const isRemoveFigmaModalModal = (
  data: unknown
): data is {
  id: string;
} => {
  const castedData = data as { id: string };

  return (
    'id' in castedData && !!castedData.id && typeof castedData.id === 'string'
  );
};

export const RemoveFigmaReportModalContent = () => {
  const { t } = useI18n();
  const { modalData, openedModalId, closeModal } = useModalWrapperContext();
  const { teamId, workspaceId } = useParams<{
    teamId: string;
    workspaceId: string;
  }>();

  const { id } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isRemoveFigmaModalModal(currentModalData)) {
      return currentModalData;
    }

    return { id: null };
  }, [modalData, openedModalId]);

  const {
    mutate: deleteReport,
    isPending,
    isSuccess,
  } = useDeleteFigmaReportMutation(workspaceId, teamId);

  useEffect(() => {
    if (!isSuccess) return;

    closeModal();
  }, [isSuccess, closeModal]);

  const handleDelete = () => {
    if (!id) return;

    deleteReport(id);
  };

  const handleCancel = () => {
    closeModal();
  };

  return (
    <Modal>
      <Modal.Header title="prototypes:report.delete.title" />
      <Modal.Content variant="narrow">
        <Typography>{t('prototypes:report.delete.text')}</Typography>
      </Modal.Content>
      <Modal.Controls>
        <ButtonsContainer>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleCancel}
            disabled={isPending}
          >
            {t('shared:cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleDelete}
            disabled={isPending || !id}
          >
            {t('shared:delete')}
          </Button>
        </ButtonsContainer>
      </Modal.Controls>
    </Modal>
  );
};
